import { type SellerUnitAndProject } from "@/types/sup";
import { storage } from "@/lib/storage";
import { trackEvent } from "@/utils/analytics/track-event";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

export interface ComparisonState {
	// Comparison tray management
	comparisonTray: SellerUnitAndProject[];
	isComparisonMinimized: boolean;

	// Actions
	initComparisonTray: (properties: SellerUnitAndProject[]) => void;
	setComparisonTray: (properties: SellerUnitAndProject[]) => void;
	toggleComparison: (property: SellerUnitAndProject) => void;
	clearComparisonTray: () => void;
	setIsComparisonMinimized: (isMinimized: boolean) => void;
}

export const useComparisonStore = create<ComparisonState>()(
	devtools(
		subscribeWithSelector(
			immer((set, _get) => ({
				comparisonTray: [],
				isComparisonMinimized: true,

				// Comparison tray actions
				initComparisonTray: properties =>
					set(state => {
						if (JSON.stringify(state.comparisonTray) === JSON.stringify(properties)) return;
						state.comparisonTray = properties;

						// Track initialization
						trackEvent({
							google: {
								event: "init_comparison_tray",
								options: {
									property_count: properties.length,
									source: "storage_restore",
								},
							},
						});
					}),

				setComparisonTray: properties =>
					set(state => {
						if (JSON.stringify(state.comparisonTray) === JSON.stringify(properties)) return;
						state.comparisonTray = properties;

						// Track comparison tray update
						trackEvent({
							google: {
								event: "set_comparison_tray",
								options: {
									property_count: properties.length,
								},
							},
						});

						if (typeof window !== "undefined") {
							storage.setItem("comparison_tray", properties).catch(error => {
								// eslint-disable-next-line no-console
								console.warn("Failed to save comparison tray to storage:", error);

								// Track storage failure
								trackEvent({
									google: {
										event: "comparison_storage_error",
										options: {
											action: "save",
											property_count: properties.length,
											error_type: "storage_save_failed",
										},
									},
								});
							});
						}
					}),

				toggleComparison: property =>
					set(state => {
						const idx = state.comparisonTray.findIndex(item => item._id === property._id);
						const action = idx !== -1 ? "remove" : "add";

						if (idx !== -1) {
							state.comparisonTray.splice(idx, 1);
						} else {
							state.comparisonTray.push(property);
						}

						// Track add/remove from comparison (store-level)
						trackEvent({
							google: {
								event: action === "add" ? "add_to_comparison_store" : "remove_from_comparison_store",
								options: {
									property_id: property._id,
									property_type: property.type,
									property_count: state.comparisonTray.length,
									source: "comparison_store",
								},
							},
						});

						// Save to storage (IndexedDB) - must happen synchronously in set
						if (typeof window !== "undefined") {
							// Create a copy of the array to save
							const dataToSave = [...state.comparisonTray];

							storage.setItem("comparison_tray", dataToSave).catch(error => {
								// Log minimal error info without exposing sensitive data
								// eslint-disable-next-line no-console
								console.error(
									"Failed to save comparison data:",
									error instanceof Error ? error.message : "Unknown error",
								);

								// Track storage failure
								trackEvent({
									google: {
										event: "comparison_storage_error",
										options: {
											action: "save",
											property_count: dataToSave.length,
											error_type: "storage_save_failed",
										},
									},
								});
							});
						}
					}),

				clearComparisonTray: () =>
					set(state => {
						const previousCount = state.comparisonTray.length;
						state.comparisonTray = [];

						// Track clear action
						trackEvent({
							google: {
								event: "clear_comparison_tray_store",
								options: {
									previous_count: previousCount,
								},
							},
						});

						if (typeof window !== "undefined") {
							storage.removeItem("comparison_tray").catch(error => {
								// eslint-disable-next-line no-console
								console.warn("Failed to clear comparison tray from storage:", error);

								// Track storage failure
								trackEvent({
									google: {
										event: "comparison_storage_error",
										options: {
											action: "clear",
											previous_count: previousCount,
											error_type: "storage_clear_failed",
										},
									},
								});
							});
						}
					}),

				setIsComparisonMinimized: isMinimized =>
					set(state => {
						state.isComparisonMinimized = isMinimized;

						// Track minimize/expand action
						trackEvent({
							google: {
								event: isMinimized ? "minimize_comparison_tray_store" : "expand_comparison_tray_store",
								options: {
									property_count: state.comparisonTray.length,
									source: "comparison_store",
								},
							},
						});
					}),
			})),
		),
		{ name: "comparison-store" },
	),
);
